import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import SignInDialog from '../../components/common/SignInDialog';
import api from '../../redux/features/auth/axiosAuthHelper';
import DeviceDetailCard from '../../components/common/cards/DeviceDetailCard';
import RecordDetailDialog from '../../components/common/RecordDetailDialog';
import DeviceTable from '../../components/common/tables/DeviceTable';
import { useDevicesTable } from '../useInventoryTables';

const DevicesPage = () => {
  const authData = useSelector((state: RootState) => state.auth);
  const table = useDevicesTable(authData.isAuthenticated, authData.access);
  const [device, setDevice] = useState<any>(null);
  const [popup, setPopup] = useState('');
  const [showGlobalFilter, setShowGlobalFilter] = useState(true);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const { paramID } = useParams<{ paramID: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const openedFromTable = useRef<string | null>(null);
  useEffect(() => {
    if (location.state?.fromTable) {
      openedFromTable.current = paramID ?? null;
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    if (paramID && openedFromTable.current === paramID) {
      openedFromTable.current = null;
      return;
    }
    openedFromTable.current = null;
    if (paramID?.toLowerCase() === 'create') {
      setPopup('Create');
      setDevice(null);
    } else if (paramID && /^\d+$/.test(paramID)) {
      setPopup('');
      setDevice(null);
      let active = true;
      api.get(`devices/${paramID}/`, {
        headers: { Authorization: `Bearer ${authData.access}` },
      }).then(response => {
        if (!active) return;
        setDevice(response.data);
        setPopup('Edit');
      }).catch(error => {
        if (!active) return;
        setDevice({ id: paramID, name: 'Not Found' });
        setPopup('Edit');
        console.error('Error fetching device:', error);
      });
      return () => { active = false; };
    } else if (paramID) {
      navigate('/phone-configuration', { replace: true });
    }
  }, [paramID, location.pathname, location.state, navigate, authData.access]);

  const handleRefreshButton = () => {
    setDevice(null);
    setPopup('');
    navigate('/phone-configuration', { replace: true });
    table.refresh();
  };

  const handleBackButton = () => {
    setDevice(null);
    setPopup('');
    navigate('/phone-configuration', { replace: true });
  };

  const openEditPage = (row: any) => {
    setDevice(row.original);
    setPopup('Edit');
    navigate(`/phone-configuration/${row.original.id}`, { replace: true, state: { fromTable: true } });
  };

  const openCreatePage = () => {
    setDevice(null);
    setPopup('Create');
    navigate('/phone-configuration/create', { replace: true });
  };

  return authData.isAuthenticated === false ? (
    <SignInDialog isAuthenticated={authData.isAuthenticated} />
  ) : authData.isAuthenticated ? (
    <div data-testid="div-9bus" style={{ overflowX: "hidden", maxHeight: `100%`, minHeight: `100%`, maxWidth: `100%`, minWidth: `100%`, margin: "auto" }}>

      <Box sx={{ p: { xs: 1, md: 1.5 }, height: '100dvh', minHeight: 360, boxSizing: 'border-box', minWidth: 0 }}>
                    <DeviceTable
                      rows={table.rows}
                      rowCount={table.rowCount}
                      isLoading={table.isLoading}
                      hasLoaded={table.hasLoaded}
                      error={table.error}
                      refresh={table.refresh}
                      sorting={table.sorting}
                      columnVisibility={columnVisibility}
                      columnFilters={table.columnFilters}
                      pagination={table.pagination}
                      globalFilter={table.globalFilter}
                      rowSelection={table.rowSelection}
                      setRowSelection={table.setRowSelection}
                      showGlobalFilter={showGlobalFilter}
                      setShowGlobalFilter={setShowGlobalFilter}
                      handleRemoveAllFilters={table.clearFilters}
                      setGlobalFilter={table.onGlobalFilterChange}
                      setSorting={table.onSortingChange}
                      setColumnVisibility={setColumnVisibility}
                      setColumnFilters={table.onColumnFiltersChange}
                      setPagination={table.onPaginationChange}
                      handleRefreshButton={handleRefreshButton}
                      openEditPage={openEditPage}
                      openCreatePage={openCreatePage}
                    />
      </Box>
      <RecordDetailDialog open={(!!device && popup === 'Edit') || popup === 'Create'} title={popup === 'Create' ? 'Create Phone Configuration' : 'Edit Phone Configuration'} onClose={handleBackButton} compactHeader>
        {popup === 'Create' ? (
          <DeviceDetailCard handleRefreshButton={handleRefreshButton} handleBackButton={handleBackButton} />
        ) : device ? (
          <DeviceDetailCard device_id={device.id} handleRefreshButton={handleRefreshButton} handleBackButton={handleBackButton} onStatusChanged={table.refresh} />
        ) : null}
      </RecordDetailDialog>
    </div>
  ) : null;
};

export default DevicesPage;
